import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
const STORAGE_KEY = 'astriqo_astrologer_profile';
export interface AstrologerService {
  id: number;
  name: string;
  price: string;
  duration: string;
  isTop: boolean;
}
export interface AstrologerContact {
  id: string;
  label: string;
  code: string;
  color: string;
  value: string;
  enabled: boolean;
}
export interface AstrologerProfileData {
  firstName: string;
  lastName: string;
  aboutText: string;
  specializations: string[];
  avatarUrl: string | null;
  experience: string;
  languages: string;
  services: AstrologerService[];
  contacts: AstrologerContact[];
  consultPrice: string;
  consultPriceType: 'fixed' | 'from';
  consultDuration: string;
  consultFormats: {
    video: boolean;
    chat: boolean;
    audio: boolean;
  };
  consultButtonText: string;
  showChatButton: boolean;
  showBookButton: boolean;
  lastUpdated: number;
}
const DEFAULT_PROFILE: AstrologerProfileData = {
  firstName: 'Елена',
  lastName: 'Смирнова',
  aboutText: 'Привет! Я Елена, профессиональный астролог с 8-летним стажем. Моя цель — перевести сложный язык звёзд на понятный человеческий. В своей работе я использую методы классической западной астрологии, подкрепленные знаниями в области психологии.',
  specializations: ['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия'],
  avatarUrl: null,
  experience: '8',
  languages: 'Русский, Английский',
  services: [{
    id: 1,
    name: 'Разбор натальной карты (Видео)',
    price: '5 000 ₽',
    duration: '60 мин',
    isTop: true
  }, {
    id: 2,
    name: 'Соляр на год (Чат)',
    price: '4 000 ₽',
    duration: '3 дня',
    isTop: false
  }, {
    id: 3,
    name: 'Синастрия',
    price: '6 000 ₽',
    duration: '90 мин',
    isTop: false
  }],
  contacts: [{
    id: 'tg',
    label: 'Telegram',
    code: 'TG',
    color: 'bg-sky-100 text-sky-600',
    value: '@elena_astro',
    enabled: true
  }, {
    id: 'wa',
    label: 'WhatsApp',
    code: 'WA',
    color: 'bg-emerald-100 text-emerald-600',
    value: '+7 999 123-45-67',
    enabled: true
  }, {
    id: 'ig',
    label: 'Instagram',
    code: 'IG',
    color: 'bg-pink-100 text-pink-600',
    value: 'elena_stars',
    enabled: false
  }, {
    id: 'web',
    label: 'Сайт',
    code: '🌐',
    color: 'bg-slate-100 text-slate-600',
    value: 'https://elena-astro.ru',
    enabled: true
  }, {
    id: 'email',
    label: 'Email',
    code: '@',
    color: 'bg-blue-100 text-blue-600',
    value: 'elena@astro.ru',
    enabled: true
  }],
  consultPrice: '5000',
  consultPriceType: 'from',
  consultDuration: '60',
  consultFormats: {
    video: true,
    chat: true,
    audio: false
  },
  consultButtonText: 'Записаться на консультацию',
  showChatButton: true,
  showBookButton: true,
  lastUpdated: 0
};
interface AstrologerProfileContextType {
  profile: AstrologerProfileData;
  updateProfile: (updates: Partial<AstrologerProfileData>) => void;
  resetProfile: () => void;
}
const AstrologerProfileContext = createContext<AstrologerProfileContextType | undefined>(undefined);
export function AstrologerProfileProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [profile, setProfile] = useState<AstrologerProfileData>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as AstrologerProfileData;
        return {
          ...DEFAULT_PROFILE,
          ...parsed
        };
      }
    } catch {}
    return DEFAULT_PROFILE;
  });
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch {}
  }, [profile]);
  const updateProfile = useCallback((updates: Partial<AstrologerProfileData>) => {
    setProfile(prev => ({
      ...prev,
      ...updates,
      lastUpdated: Date.now()
    }));
  }, []);
  const resetProfile = useCallback(() => {
    setProfile(DEFAULT_PROFILE);
    localStorage.removeItem(STORAGE_KEY);
  }, []);
  return <AstrologerProfileContext.Provider value={{
    profile,
    updateProfile,
    resetProfile
  }} data-id="element-19493">
      {children}
    </AstrologerProfileContext.Provider>;
}
export function useAstrologerProfile() {
  const context = useContext(AstrologerProfileContext);
  if (context === undefined) {
    throw new Error('useAstrologerProfile must be used within AstrologerProfileProvider');
  }
  return context;
}